/**
 * The terminal state, and the net that catches what would otherwise reach only
 * the console.
 *
 * `VaultWorkerClient.close()` latches its failure on purpose: a trapped wasm
 * worker must not be retried silently. But a latch nobody reports leaves the
 * app permanently inert and perfectly quiet — the owner taps Sync, the spinner
 * turns and stops, every later click also does nothing, and the only way out is
 * guessing to reload. That is the worst failure mode available to a client
 * whose whole promise is being usable all day, so the latch gets a surface.
 *
 * Two things live here. The surface itself, which start-up already needed and
 * built inline. And the global listeners, because a promise nobody awaited is
 * exactly how the worker's rejections were being lost.
 */
import { escapeHtml } from "./vault.js";

/** What the owner is told when the app cannot carry on. */
export interface FatalState {
  readonly headline: string;
  /** Why, in the words the failure itself used where there are any. */
  readonly detail: string;
}

/**
 * A vault worker that has stopped.
 *
 * `close()` already writes a user-facing sentence for each cause — a stalled
 * initialisation, an unreadable response, a worker that would not start — so
 * the detail is its message rather than a second guess at what happened. The
 * reassurance is added here because it is true of every cause: the latch stops
 * the worker, and nothing in `close()` touches stored data.
 */
export function workerStopped(error: Error): FatalState {
  const detail = error.message.trim();
  return {
    headline: "The vault worker stopped.",
    detail: `${detail === "" ? "The worker ended without saying why." : detail} Your saved notes are still on this device — reloading reopens them.`,
  };
}

export function fatalStateHtml(state: FatalState): string {
  return `<main class="fatal"><h1>${escapeHtml(state.headline)}</h1><p>${escapeHtml(state.detail)}</p><button type="button" data-reload>Reload</button></main>`;
}

/**
 * Replaces the app with the terminal state and its one way out.
 *
 * This deliberately discards the shell. A latched worker means every control
 * still on screen is dead, and leaving them clickable is what made the failure
 * invisible in the first place.
 */
export function showFatalState(root: Element, state: FatalState, reload: () => void): void {
  root.innerHTML = fatalStateHtml(state);
  root.querySelector("[data-reload]")?.addEventListener("click", () => reload());
}

/** The `addEventListener` shape this needs, so a test can supply its own. */
export interface ErrorEventTarget {
  addEventListener(type: string, listener: (event: Event) => void): void;
}

/**
 * Routes failures nobody handled to `report`.
 *
 * `unhandledrejection` is the one that matters: every `void someAsync()` in the
 * shell lands here when it rejects. The `error` listener catches a synchronous
 * throw from a handler, which is silent in the same way.
 *
 * Resource load failures are ignored. A missing image or a blocked font fires
 * `error` on the element, not the window, and bubbles up here carrying no
 * error at all; treating one as an app failure would put the terminal state on
 * screen because a favicon 404'd.
 */
export function installUnhandledReporter(target: ErrorEventTarget, report: (reason: unknown) => void): void {
  target.addEventListener("unhandledrejection", (event: Event) => {
    // Reported, so it is no longer unhandled; without this the console still
    // logs it as uncaught alongside the surface the owner is now looking at.
    event.preventDefault();
    report((event as PromiseRejectionEvent).reason);
  });
  target.addEventListener("error", (event: Event) => {
    if (event.target !== null && event.target !== target) return;
    const error = (event as ErrorEvent).error;
    report(error ?? (event as ErrorEvent).message ?? new Error("Something failed without saying what."));
  });
}
